import { LoadingOutlined } from "@ant-design/icons";
import { useModal } from "@ebay/nice-modal-react";
import { Button, Col, Form, Input, message, Row, Select, Spin } from "antd";
import LabelValue from "components/foundation/input/LabelValue";
import BaseModal from "components/foundation/modal/BaseModal";
import SelectFormItemV2 from "components/foundation/selected/SelectFormItemV2";
import React, { useEffect, useMemo } from "react";
import { SIMTYPE, TYPEGTTT } from "utils/Constants/Constants";
import Format from "utils/format/Format";
import useDKTTBInfoModel from "../../viewmodel/model/dkttb/DKTTBInfoModel";
import ImgDetail from "../modal/ImgDetail";
import PlanSelectModal from "../modal/PlanSelectModal";
import { useAuth } from "global/context/AuthenticationContext";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 42,
      color: "var(--mk-p-color)",
    }}
    spin
  />
);

const OrderDetail: React.FC = () => {
  const [form] = Form.useForm();
  const { fee } = useAuth();
  const userModal = useModal(BaseModal);

  const { plans, loading, detailDK, resultObject, onConfirm } =
    useDKTTBInfoModel();

  const imgSimValue = Form.useWatch("imgSim", form);
  const imgCccdFrontValue = Form.useWatch("imgCccdFront", form);
  const imgCccdBackValue = Form.useWatch("imgCccdBack", form);
  const imgPortraitValue = Form.useWatch("imgPortrait", form);
  const imgSignatureValue = Form.useWatch("imgSignature", form);
  const planIdValue = Form.useWatch("plan_id", form);
  const simTypeValue = Form.useWatch("sim_type", form);
  const isRetry = detailDK?.is_retry === true && detailDK?.is_public !== true;

  useEffect(() => {
    if (!detailDK || Object.keys(detailDK).length === 0) return;

    if (isRetry) {
      form.resetFields([
        "plan_id",
        "sim_type",
        "serial",
        "email",
        "address",
        "imgSim",
        "imgCccdFront",
        "imgCccdBack",
        "imgPortrait",
        "imgSignature",
      ]);
      return;
    }

    const formData: any = {};

    if (detailDK.plan_id?.trim()) {
      formData.plan_id = detailDK.plan_id;
    }

    if (
      detailDK.sim_type &&
      detailDK.sim_type !== -1 &&
      detailDK.sim_type !== "UNKNOW"
    ) {
      formData.sim_type = detailDK.sim_type;
    }

    if (detailDK.serial?.trim()) {
      formData.serial = detailDK.serial;
    }

    form.setFieldsValue(formData);
  }, [detailDK, form, isRetry]);

  // useEffect(() => {
  //   if (!detailDK || Object.keys(detailDK).length === 0) return;

  //   const formData: any = {};
  //   if (detailDK.plan_name?.trim()) formData.plan_id = detailDK.plan_name;
  //   else if (detailDK.plan_id?.trim()) formData.plan_id = detailDK.plan_id;

  //   if (
  //     detailDK.sim_type &&
  //     detailDK.sim_type !== -1 &&
  //     detailDK.sim_type !== "UNKNOW"
  //   ) {
  //     formData.sim_type = detailDK.sim_type;
  //   }

  //   if (detailDK.serial?.trim()) formData.serial = detailDK.serial;

  //   form.setFieldsValue(formData);
  // }, [detailDK, form]);

  const priceSummary = useMemo(() => {
    const esimFee =
      simTypeValue === "ESIM" && !detailDK?.is_public
        ? fee?.esimActivation || 0
        : 0;

    const selectedPlan = plans.find(
      (p: any) => p.id === planIdValue || p.name === planIdValue,
    );

    const currentPlanPrice = selectedPlan
      ? selectedPlan.price
      : detailDK?.plan_price || 0;

    const baseAmount = detailDK?.total_amount || 0;

    // Fix lỗi hiển thị sai số tiền, chưa biết tại sao lại +
    const newTotalAmount = baseAmount; //+ currentPlanPrice + esimFee;

    return {
      planPrice: currentPlanPrice,
      esimFee,
      totalAmount: newTotalAmount,
    };
  }, [planIdValue, plans, detailDK, simTypeValue, fee]);

  const handleImg = (info: any, field: any) => {
    form.setFieldsValue({ [field]: info });
  };

  const onClose = async () => {
    userModal.hide();
  };

  const detail = (info: any, field: any, type?: any) => {
    const hasImage = !!info;

    userModal
      .show({
        title: hasImage ? "Xem chi tiết" : "Tải ảnh lên",
        children: (
          <ImgDetail
            img={info}
            field={field}
            onClick={handleImg}
            onClose={onClose}
            type={type}
          />
        ),
        className: "modal-40",
        footer: hasImage
          ? [
              {
                title: "Đóng",
                htmlType: "button",
                type: "primary",
              },
            ]
          : [],
      })
      .then((values: any) => {})
      .catch((e) => {});
  };

  const handleConfirm = () => {
    const info = form.getFieldsValue();

    if (simTypeValue === "ESIM") {
      info.serial = undefined;
    }

    onConfirm(info);
  };

  useEffect(() => {
    if (!!resultObject?.message) {
      switch (resultObject?.type) {
        case "success":
          message.success(resultObject?.message);
          break;
        case "error":
          message.error(resultObject?.message);
          break;
        default:
          break;
      }
    }
  }, [resultObject]);

  // useEffect(() => {
  //   if (simTypeValue === "ESIM") {
  //     form.setFieldsValue({
  //       serial: undefined,
  //     });
  //   }
  // }, [simTypeValue]);

  return (
    <div className="p-[20px] dkttb">
      <Spin
        spinning={loading}
        indicator={antIcon}
        tip={<span style={{ color: "var(--mk-p-color)" }}>Đang tải...</span>}
        size="large"
      >
        <Form layout="horizontal" form={form} onFinish={handleConfirm}>
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <div className="p-[15px] md:p-[30px] bg-white rounded-3xl shadow-md">
                <div className="text-[18px] font-bold border-b-[3px] border-gray-300 pb-2 mb-4">
                  Thông tin chi tiết
                </div>
                <LabelValue
                  label="Cửa hàng"
                  value={detailDK?.store_name}
                  sm={9}
                  bold="false"
                  textSize={15}
                  textAlign="right"
                  className="custom-form-item"
                />
                <LabelValue
                  label="Thời gian tạo đơn"
                  value={Format.formatFullDateTime(detailDK?.created_at)}
                  sm={9}
                  bold="false"
                  textSize={15}
                  textAlign="right"
                  className="custom-form-item"
                />
                <LabelValue
                  label="Mã đơn"
                  value={detailDK?.code}
                  sm={9}
                  bold="false"
                  textSize={15}
                  textAlign="right"
                  className="custom-form-item"
                />

                <LabelValue
                  label="Số điện thoại"
                  value={detailDK?.phone_number}
                  sm={9}
                  bold="true"
                  textSize={15}
                  textAlign="right"
                  className="custom-form-item"
                  valueColor="green"
                />

                <SelectFormItemV2
                  title="Loại sim"
                  id="sim_type"
                  size="middle"
                  className="custom-form-item"
                  form={form}
                  // onChange={(value) => {
                  //   form?.setFieldsValue({ sim_type: value.value });
                  // }}
                  onChange={(value) => {
                    form.setFieldsValue({
                      sim_type: value.value,
                      serial: undefined,
                      imgSim: undefined,
                      email: undefined,
                      address: undefined,
                    });
                  }}
                  data={SIMTYPE}
                  colon={false}
                  bordered={false}
                  // initialValue={detailDK?.sim_type}
                />

                {simTypeValue === "ESIM" && (
                  <>
                    <Form.Item
                      label="Email"
                      name="email"
                      labelCol={{ span: 10 }}
                      wrapperCol={{ span: 14 }}
                      colon={false}
                      className="custom-form-item"
                      rules={[
                        { required: true, message: "Vui lòng nhập email" },
                        { type: "email", message: "Email không hợp lệ" },
                      ]}
                    >
                      <Input
                        type="email"
                        placeholder="Nhập email"
                        className="border-none shadow-none text-right"
                      />
                    </Form.Item>
                    <Form.Item
                      label="Địa chỉ"
                      name="address"
                      labelCol={{ span: 10 }}
                      wrapperCol={{ span: 14 }}
                      className="custom-form-item"
                      colon={false}
                      rules={[
                        { required: true, message: "Vui lòng nhập địa chỉ" },
                      ]}
                    >
                      <Input
                        placeholder="Nhập địa chỉ"
                        className="border-none shadow-none text-right"
                      />
                    </Form.Item>
                  </>
                )}

                {simTypeValue !== "ESIM" && (
                  <Form.Item
                    label="Serial"
                    name="serial"
                    labelCol={{ span: 10 }}
                    wrapperCol={{ span: 14 }}
                    className="custom-form-item"
                    colon={false}
                    rules={[
                      { required: true, message: "Vui lòng nhập serial" },
                    ]}
                  >
                    <Input
                      placeholder="Nhập serial"
                      className="border-none shadow-none text-right"
                      // disabled={!!detailDK?.serial}
                      disabled={!isRetry && !!detailDK?.serial}
                    />
                  </Form.Item>
                )}

                <PlanSelectModal
                  plans={plans}
                  form={form}
                  detailDK={detailDK}
                />
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <div className="p-[15px] md:p-[30px] bg-white rounded-3xl shadow-md mb-4">
                <div className="text-[18px] font-bold border-b-[3px] border-gray-300 pb-2 mb-4">
                  Chi tiết giá tiền
                </div>
                {detailDK?.is_public === true ? (
                  <>
                    <LabelValue
                      label="Giá sim cần thanh toán (90% giá sim)"
                      value={Format.Price(detailDK?.price)}
                      sm={14}
                      bold="left"
                      textSize={13}
                      textAlign="right"
                      className="custom-form-item"
                    />
                  </>
                ) : (
                  <>
                    <LabelValue
                      label="Phí"
                      value={Format.Price(detailDK?.price)}
                      sm={9}
                      bold="left"
                      textSize={15}
                      textAlign="right"
                      className="custom-form-item"
                    />

                    <LabelValue
                      label="Phí hòa mạng"
                      value={Format.Price(detailDK?.activation_fee)}
                      sm={9}
                      bold="false"
                      textSize={15}
                      textAlign="right"
                      className="custom-form-item"
                    />
                    <LabelValue
                      label="Giá phân phối"
                      value={Format.Price(detailDK?.fee)}
                      sm={9}
                      bold="false"
                      textSize={15}
                      textAlign="right"
                      className="custom-form-item"
                    />
                    <LabelValue
                      label="Gói cước"
                      value={Format.Price(priceSummary.planPrice)}
                      sm={9}
                      textAlign="right"
                    />
                    {simTypeValue === "ESIM" && (
                      <LabelValue
                        label="Phí Esim"
                        value={Format.Price(priceSummary.esimFee)}
                        sm={9}
                        textAlign="right"
                      />
                    )}
                  </>
                )}

                <hr className="!border-gray-300 pb-2 mb-2" />
                <LabelValue
                  label="Tổng tiền thanh toán"
                  value={Format.Price(priceSummary.totalAmount)}
                  sm={12}
                  bold="true"
                  color="#FF0606"
                  textAlign="right"
                />
              </div>
              <div className="p-[15px] md:p-[30px] bg-white rounded-3xl shadow-md">
                <div className="text-[18px] font-bold border-b-[3px] border-gray-300 pb-2 mb-4">
                  Thông tin đăng ký
                </div>

                <Form.Item
                  label="Chọn loại GTTT"
                  name="id_type"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  rules={[
                    { required: true, message: "Vui lòng nhập Chọn loại GTTT" },
                  ]}
                >
                  <Select
                    bordered={false}
                    placeholder="Chọn loại GTTT"
                    labelInValue
                    onChange={(value) => {
                      form?.setFieldsValue({ id_type: value.value });
                    }}
                    options={TYPEGTTT}
                  />
                </Form.Item>

                <Form.Item
                  label="Ảnh phôi sim"
                  name="imgSim"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  rules={[
                    { required: true, message: "Vui lòng nhập Ảnh phôi sim" },
                  ]}
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgSimValue ? "Đã có" : "Chưa có"}
                    </div>
                    <div style={{ display: "flex", gap: "100px" }}>
                      {imgSimValue && (
                        <div
                          className="fake-btn-link"
                          onClick={() => detail(imgSimValue, "imgSim")}
                        >
                          <span className="desktop-text">Xem chi tiết</span>
                          <span className="mobile-text">Xem</span>
                        </div>
                      )}

                      <div
                        className="fake-btn-link"
                        onClick={() => detail(null, "imgSim")}
                      >
                        {imgSimValue ? (
                          <>
                            <span className="desktop-text">Sửa hình ảnh</span>
                            <span className="mobile-text">Sửa</span>
                          </>
                        ) : (
                          <>
                            <span className="desktop-text">Tải hình ảnh</span>
                            <span className="mobile-text">Tải</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh CCCD mặt trước"
                  name="imgCccdFront"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập Ảnh CCCD mặt trước",
                    },
                  ]}
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgCccdFrontValue ? "Đã có" : "Chưa có"}
                    </div>
                    <div style={{ display: "flex", gap: "100px" }}>
                      {imgCccdFrontValue && (
                        <div
                          className="fake-btn-link"
                          onClick={() =>
                            detail(imgCccdFrontValue, "imgCccdFront")
                          }
                        >
                          <span className="desktop-text">Xem chi tiết</span>
                          <span className="mobile-text">Xem</span>
                        </div>
                      )}

                      <div
                        className="fake-btn-link"
                        onClick={() => detail(null, "imgCccdFront")}
                      >
                        {imgCccdFrontValue ? (
                          <>
                            <span className="desktop-text">Sửa hình ảnh</span>
                            <span className="mobile-text">Sửa</span>
                          </>
                        ) : (
                          <>
                            <span className="desktop-text">Tải hình ảnh</span>
                            <span className="mobile-text">Tải</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh CCCD mặt sau"
                  name="imgCccdBack"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập Ảnh CCCD mặt sau",
                    },
                  ]}
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgCccdBackValue ? "Đã có" : "Chưa có"}
                    </div>
                    <div style={{ display: "flex", gap: "100px" }}>
                      {imgCccdBackValue && (
                        <div
                          className="fake-btn-link"
                          onClick={() =>
                            detail(imgCccdBackValue, "imgCccdBack")
                          }
                        >
                          <span className="desktop-text">Xem chi tiết</span>
                          <span className="mobile-text">Xem</span>
                        </div>
                      )}

                      <div
                        className="fake-btn-link"
                        onClick={() => detail(null, "imgCccdBack")}
                      >
                        {imgCccdBackValue ? (
                          <>
                            <span className="desktop-text">Sửa hình ảnh</span>
                            <span className="mobile-text">Sửa</span>
                          </>
                        ) : (
                          <>
                            <span className="desktop-text">Tải hình ảnh</span>
                            <span className="mobile-text">Tải</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh chân dung"
                  name="imgPortrait"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập Ảnh chân dung",
                    },
                  ]}
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgPortraitValue ? "Đã có" : "Chưa có"}
                    </div>
                    <div style={{ display: "flex", gap: "100px" }}>
                      {imgPortraitValue && (
                        <div
                          className="fake-btn-link"
                          onClick={() =>
                            detail(imgPortraitValue, "imgPortrait")
                          }
                        >
                          <span className="desktop-text">Xem chi tiết</span>
                          <span className="mobile-text">Xem</span>
                        </div>
                      )}

                      <div
                        className="fake-btn-link"
                        onClick={() => detail(null, "imgPortrait")}
                      >
                        {imgPortraitValue ? (
                          <>
                            <span className="desktop-text">Sửa hình ảnh</span>
                            <span className="mobile-text">Sửa</span>
                          </>
                        ) : (
                          <>
                            <span className="desktop-text">Tải hình ảnh</span>
                            <span className="mobile-text">Tải</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh chữ ký"
                  name="imgSignature"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập Ảnh chữ ký",
                    },
                  ]}
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgSignatureValue ? "Đã có" : "Chưa có"}
                    </div>
                    <div style={{ display: "flex", gap: "100px" }}>
                      {imgSignatureValue && (
                        <div
                          className="fake-btn-link"
                          onClick={() =>
                            detail(imgSignatureValue, "imgSignature")
                          }
                        >
                          <span className="desktop-text">Xem chi tiết</span>
                          <span className="mobile-text">Xem</span>
                        </div>
                      )}

                      <div
                        className="fake-btn-primary"
                        onClick={() => detail(null, "imgSignature", "sign")}
                      >
                        {imgSignatureValue ? (
                          <>
                            <span className="desktop-text">Sửa chữ ký</span>
                            <span className="mobile-text">Sửa</span>
                          </>
                        ) : (
                          <>
                            <span className="desktop-text">Ký ngay</span>
                            <span className="mobile-text">Ký</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Form.Item>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                block
                className="mt-4 p-[25px] text-[20px]"
                loading={loading}
              >
                Tiếp tục
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default OrderDetail;
